import { useCallback, useMemo } from 'react';
import { generateArrayItemDefault } from "../utils/defaults.js";
import { getPath, joinPath } from "../utils/path.js";
import { resolveRef } from "../utils/schema-context.js";
import { useFormStore, useFormStoreSelector } from "./use-form-store.js";
import { useSchemaContext } from "./use-schema-context.js";
const EMPTY_ARRAY = [];
const EMPTY_KEYS = [];
export function useArrayField({ path, schema }) {
    const store = useFormStore();
    const ctx = useSchemaContext();
    const arrayValue = useFormStoreSelector((s) => getPath(s.values, path) ?? EMPTY_ARRAY);
    const arrayKeys = useFormStoreSelector((s) => s.arrayKeys[path] ?? EMPTY_KEYS);
    // Resolve the array schema and extract item schema
    const resolvedArraySchema = useMemo(() => resolveRef(ctx, schema), [ctx, schema]);
    // Extract the raw item schema (before resolution) to preserve $ref
    const rawItemSchema = useMemo(() => {
        if (!resolvedArraySchema?.items)
            return undefined;
        return (Array.isArray(resolvedArraySchema.items) ? resolvedArraySchema.items[0] : resolvedArraySchema.items);
    }, [resolvedArraySchema]);
    // Get the original $ref before resolution (for form config lookup)
    const itemSchemaRef = rawItemSchema?.$ref;
    // Resolve the item schema
    const itemSchema = useMemo(() => {
        if (!rawItemSchema)
            return undefined;
        return resolveRef(ctx, rawItemSchema);
    }, [ctx, rawItemSchema]);
    const items = useMemo(() => arrayValue.map((value, index) => ({
        key: arrayKeys[index] ?? `item-${index}`,
        index,
        path: joinPath(path, index),
        value
    })), [arrayValue, arrayKeys, path]);
    const append = useCallback((value) => {
        const newValue = value ?? generateArrayItemDefault(resolvedArraySchema, ctx);
        store.getState().arrayAppend(path, newValue);
    }, [store, path, resolvedArraySchema, ctx]);
    const insert = useCallback((index, value) => {
        const newValue = value ?? generateArrayItemDefault(resolvedArraySchema, ctx);
        store.getState().arrayInsert(path, index, newValue);
    }, [store, path, resolvedArraySchema, ctx]);
    const remove = useCallback((index) => store.getState().arrayRemove(path, index), [store, path]);
    const move = useCallback((from, to) => store.getState().arrayMove(path, from, to), [store, path]);
    const swap = useCallback((indexA, indexB) => store.getState().arraySwap(path, indexA, indexB), [store, path]);
    const replace = useCallback((newItems) => store.getState().setFieldValue(path, newItems), [store, path]);
    const clear = useCallback(() => store.getState().setFieldValue(path, []), [store, path]);
    return {
        items,
        itemSchema,
        itemSchemaRef,
        length: arrayValue.length,
        append,
        insert,
        remove,
        move,
        swap,
        replace,
        clear
    };
}
